import { convertValueAndUnit } from 'src/helpers/utilities';
import type { Device } from 'src/types/Device';
import type { ConsumptionStores } from './consumption_helpers';
import { isEnergyMonitoringDemoMode } from './energy_data_mode';
import { DEMO_INSIGHTS, type DemoInsight } from './energy_demo_data';
import { calculateEnergyInsightMetrics } from './energy_insight_metrics';
import { buildLiveEnergySnapshot } from './live_energy_snapshot';

export type EnergyInsight = DemoInsight;

export const buildEnergyInsights = (
	stores: ConsumptionStores,
	devices: Record<string, Device>,
	useDemoData = isEnergyMonitoringDemoMode(),
): EnergyInsight[] => {
	if (useDemoData) {
		return DEMO_INSIGHTS;
	}

	const live = buildLiveEnergySnapshot(stores, devices);
	const { loadW, solarW, standbyW } = live;

	const { standbyPct } = calculateEnergyInsightMetrics({
		loadW,
		solarW,
		gridW: live.gridImportW,
		standbyW,
	});
	const selfPoweredPct = live.selfPoweredPct;
	const gridExposurePct = live.gridExposurePct;

	const standbyDisplay = convertValueAndUnit(standbyW, 'W');

	return [
		{
			labelKey: 'energy_monitoring_insight_self_powered',
			labelDefault: 'Self-powered',
			detailKey: 'energy_monitoring_insight_self_powered_desc',
			detailDefault: 'Live solar coverage',
			value: `${selfPoweredPct}%`,
			progress: selfPoweredPct,
			type: selfPoweredPct >= 50 ? 'success' : 'primary',
		},
		{
			labelKey: 'energy_monitoring_insight_grid',
			labelDefault: 'Grid exposure',
			detailKey: 'energy_monitoring_insight_grid_desc',
			detailDefault: 'Live grid reliance',
			value: `${gridExposurePct}%`,
			progress: gridExposurePct,
			type: gridExposurePct > 40 ? 'warning' : 'success',
		},
		{
			labelKey: 'energy_monitoring_insight_standby',
			labelDefault: 'Standby waste',
			detailKey: 'energy_monitoring_insight_standby_desc',
			detailDefault: 'Can be reduced overnight',
			value: `${standbyDisplay.value} ${standbyDisplay.unit}`,
			progress: standbyPct,
			type: standbyPct > 15 ? 'danger' : standbyPct > 0 ? 'warning' : 'primary',
		},
	];
};
